"use client";

import { Shield, Lock, Eye, UserCheck, FileText, Globe, Bell, Mail, Database, Scale } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PolicySections() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const materials = [{
    name: "Information We Collect",
    description: "We collect personal data you provide directly, including:\n•\xA0 \xA0 Contact information: name, email address, phone number\n•\xA0 \xA0 Business information: company name, title, revenue range, EBITDA range, transaction type and timeline\n•\xA0 \xA0 Communication content: messages, inquiries, and attachments you submit through our forms\n•\xA0 \xA0 Technical data: IP address, browser type, device type, pages visited, and time on site (collected automatically via analytics tools)",
    tensile: "Data Collection",
    temp: "Direct",
    Icon: Database
  }, {
    name: "How We Use Information",
    description: "We use the information we collect to:\n•\xA0 \xA0 Respond to your inquiry or consultation request\n•\xA0 \xA0 Evaluate whether a potential advisory engagement may be a fit\n•\xA0 \xA0 Send you newsletters, market intelligence, or other content you have subscribed to\n•\xA0 \xA0 Improve the functionality and content of our Site\n•\xA0 \xA0 Comply with applicable laws and regulations\nWe do not sell your personal information to third parties.",
    tensile: "Processing",
    temp: "Service-Led",
    Icon: UserCheck
  }, {
    name: "Sharing of Information",
    description: "We may share your information with:\n•\xA0 \xA0 GT Securities, Inc. — our affiliated FINRA/SIPC-registered broker-dealer, in connection with potential securities transactions or advisory engagements\n•\xA0 \xA0 Service providers — including our CRM platform (HubSpot), email service providers, and website hosting (10Web/WordPress), who process data on our behalf under confidentiality obligations\n•\xA0 \xA0 Legal and compliance advisors — where required for regulatory compliance or legal proceedings\n•\xA0 \xA0 Successors or acquirers — in the event of a merger, acquisition, or sale of substantially all of our assets",
    tensile: "Protection",
    temp: "High-Level",
    Icon: Shield
  }, {
    name: "California Consumer Privacy Act (CCPA)",
    description: "If you are a California resident, you have the following rights under the CCPA:\n•\xA0 \xA0 Right to Know: You may request disclosure of the categories and specific pieces of personal information we have collected about you.\n•\xA0 \xA0 Right to Delete: You may request deletion of your personal information, subject to certain exceptions.\n•\xA0 \xA0 Right to Opt-Out: You may opt out of the sale of your personal information. We do not sell personal information.\n•\xA0 \xA0 Right to Non-Discrimination: We will not discriminate against you for exercising your CCPA rights.\nTo exercise any of these rights, please contact us at Jeff@BPVConsulting.com or by mail at Blue Phoenix Ventures, LLC, Newport Beach, California. We will respond within 45 days as required by law.\n\n",
    tensile: "Confidentiality",
    temp: "Restricted",
    Icon: Lock
  }, {
    name: "Cookies & Analytics",
    description: "Our Site uses cookies and similar tracking technologies to analyze Site traffic and improve user experience. We use Google Analytics 4 to collect anonymized usage data. You may disable cookies in your browser settings or through our cookie consent banner. Disabling cookies may affect certain Site functionality.",
    tensile: "Analytics",
    temp: "Automated",
    Icon: Eye
  }, {
    name: "Data Security",
    description: "We implement reasonable administrative, technical, and physical safeguards to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.",
    tensile: "Compliance",
    temp: "User-Centric",
    Icon: Scale
  }, {
    name: "Third-Party Links",
    description: "Our Site may contain links to third-party websites, including FINRA BrokerCheck, GT Securities, and social media platforms. We are not responsible for the privacy practices of those sites.",
    tensile: "Global",
    temp: "Standardized",
    Icon: Globe
  }, {
    name: "Children's Privacy",
    description: "Our Site is not directed to individuals under the age of 18. We do not knowingly collect personal information from minors.",
    tensile: "Storage",
    temp: "Regulated",
    Icon: FileText
  }, {
    name: "Changes to This Policy",
    description: "We may update this Privacy Policy from time to time. The updated policy will be posted on this page with the revised effective date. We encourage you to review this policy periodically.",
    tensile: "Updates",
    temp: "Periodic",
    Icon: Bell
  }, {
    name: "Contact Us",
    description: "For questions, requests, or concerns regarding this Privacy Policy, please contact:\nBlue Phoenix Ventures, LLC\xA0 |\xA0 Newport Beach, California\xA0 |\xA0 Email: Jeff@BPVConsulting.com",
    tensile: "Support",
    temp: "Direct",
    Icon: Mail
  }];
  return <section data-section-id="2254" id="policy-sections" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--chart-1)"
          }}>
              Privacy Framework
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Commitment to data integrity and trust.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              At Blue Phoenix Ventures, we handle your information with the same rigor and 
              confidentiality we apply to our multi-million dollar M&A transactions. 
              Our policy outlines how we safeguard your legacy.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {materials.map((material, i) => {
          const Icon = material.Icon;
          return <div data-index={i} key={material.name} className="group relative rounded-lg border border-border bg-card p-7 md:p-8 transition-all duration-300 ease-out hover:-translate-y-1 shadow-md hover:shadow-lg" style={{
            backgroundColor: "var(--card)"
          }}>
                <div className="mb-7 inline-flex h-[60px] w-[60px] items-center justify-center rounded-lg transition-colors duration-300" style={{
              backgroundColor: "var(--muted)"
            }}>
                  <Icon className="h-6 w-6 transition-colors duration-300 group-hover:text-background" strokeWidth={1.25} style={{
                color: "var(--chart-1)"
              }} />
                  <span className="absolute pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-[60px] w-[60px] rounded-lg -z-0" aria-hidden="true" />
                </div>

                <style>{`
                  .group:hover [data-badge-fill="${i}"] {
                    background-color: var(--chart-1);
                  }
                `}</style>

                <h3 className="font-default font-semibold text-[28px] leading-tight text-card-foreground mb-3">
                  {material.name}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {material.description}
                </p>

                
              </div>;
        })}
        </div>
      </div>

      <style>{`
        #policy-sections .group:hover .material-badge-icon {
          color: var(--primary-foreground);
        }
      `}</style>
    </section>;
}